/**
 * Auth Status Checking
 *
 * Functions to check authentication status for factoring providers.
 * Uses the backend API directly - no extension dependency for status checking.
 */

import axios from 'axios'
import { clearFactoringDataCache } from './data'
import { clearEcapitalSessionCache, reconcileEcapitalSession } from './ecapital-session'
import {
    DEFAULT_RTS_STATUS,
    DEFAULT_TRIUMPH_STATUS,
    DEFAULT_WEX_STATUS,
    DEFAULT_WINFACTOR_STATUS,
    DEFAULT_GSQUARED_STATUS,
    DEFAULT_COMPASS_STATUS,
    DEFAULT_TAFS_STATUS,
    DEFAULT_STRATOPAY_STATUS,
    DEFAULT_ENGAGED_FINANCIAL_STATUS,
    DEFAULT_HAUL_PAY_STATUS,
    DEFAULT_OTR_SOLUTIONS_STATUS,
    DEFAULT_FLEXENT_STATUS,
    DEFAULT_BASICBLOCK_STATUS,
} from './constants'
import {
    RTSAuthStatus,
    TriumphAuthStatus,
    WexAuthStatus,
    WinfactorAuthStatus,
    GsquaredAuthStatus,
    CompassAuthStatus,
    TafsAuthStatus,
    StratopayAuthStatus,
    EngagedFinancialAuthStatus,
    HaulPayAuthStatus,
    OtrSolutionsAuthStatus,
    FlexentAuthStatus,
    BasicblockAuthStatus,
    FactoringProviderStatus,
    FactoringStatusResponse,
} from './types'

// Cache for combined status to avoid multiple API calls
let statusCache: {
    data: FactoringStatusResponse['data'] | null
    timestamp: number
} = { data: null, timestamp: 0 }

const CACHE_TTL = 30000 // 30 seconds

/**
 * Fetch combined factoring status from backend
 * Returns status for all providers in a single API call
 */
export async function fetchFactoringStatus(purpose?: 'accounting' | 'rating'): Promise<FactoringStatusResponse['data'] | null> {
    const now = Date.now()

    // Return cached data if still valid (skip cache when purpose is specified)
    if (!purpose && statusCache.data && now - statusCache.timestamp < CACHE_TTL) {
        return statusCache.data
    }

    try {
        const params = purpose ? { purpose } : {}
        const response = await axios.get<FactoringStatusResponse>('/factoring/status', { params })

        if (response.data?.success && response.data.data) {
            // The server cannot see the dispatcher's eCapital browser login, so an expired one still reads
            // "connected" there. Settings and Money Operations both come through here, so correcting it once
            // fixes both.
            const data = await reconcileEcapitalSession(response.data.data)
            // Purpose-scoped polling must not poison the shared default cache: accounting and rating
            // tokens can have different validity for the same provider.
            if (!purpose) statusCache = { data, timestamp: now }
            return data
        }

        return null
    } catch (error) {
        console.error('[FactoringService] Failed to fetch factoring status:', error)
        // Return stale cached data if available so users don't see false auth errors on transient network failures
        if (!purpose && statusCache.data) {
            console.warn('[FactoringService] Returning stale cached status due to API failure')
            return statusCache.data
        }
        return null
    }
}

/**
 * Convert backend RTS status to RTSAuthStatus
 */
function convertRTSStatus(status: FactoringProviderStatus | undefined): RTSAuthStatus {
    if (!status || !status.connected) {
        return {
            ...DEFAULT_RTS_STATUS,
            message: 'Please authorize RTS to access carrier ratings',
        }
    }

    if (status.connected && status.isValid) {
        return {
            isAuthenticated: true,
            hasValidToken: true,
            canRefresh: false,
            status: 'authenticated',
            message: 'Connected to RTS',
            needsReauth: false,
        }
    }

    return {
        isAuthenticated: false,
        hasValidToken: false,
        canRefresh: false,
        status: 'expired',
        message: 'RTS authorization expired. Please login again',
        needsReauth: true,
    }
}

/**
 * Convert backend Triumph status to TriumphAuthStatus
 */
function convertTriumphStatus(status: FactoringProviderStatus | undefined): TriumphAuthStatus {
    if (!status || !status.connected) {
        return {
            ...DEFAULT_TRIUMPH_STATUS,
            message: 'Please authorize Triumph to access factoring services',
        }
    }

    if (status.connected && status.isValid) {
        return {
            isAuthenticated: true,
            hasValidToken: true,
            canRefresh: false,
            status: 'authenticated',
            message: 'Connected to Triumph',
            needsReauth: false,
        }
    }

    return {
        isAuthenticated: false,
        hasValidToken: false,
        canRefresh: false,
        status: 'expired',
        message: 'Triumph authorization expired. Please login again',
        needsReauth: true,
    }
}

/**
 * Convert backend WEX status to WexAuthStatus
 */
function convertWexStatus(status: FactoringProviderStatus | undefined): WexAuthStatus {
    if (!status || !status.connected) {
        return {
            ...DEFAULT_WEX_STATUS,
            message: 'Please authorize WEX to access factoring services',
        }
    }

    if (status.connected && status.isValid) {
        return {
            isAuthenticated: true,
            hasValidCookies: true,
            status: 'authenticated',
            message: 'Connected to WEX',
            needsReauth: false,
        }
    }

    return {
        isAuthenticated: false,
        hasValidCookies: false,
        status: 'expired',
        message: 'WEX authorization expired. Please login again',
        needsReauth: true,
    }
}

/**
 * Check RTS auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkRTSAuthStatus(): Promise<RTSAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertRTSStatus(status?.rts)
}

/**
 * Check Triumph auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkTriumphAuthStatus(): Promise<TriumphAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertTriumphStatus(status?.triumph)
}

/**
 * Check WEX auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkWexAuthStatus(): Promise<WexAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertWexStatus(status?.wex)
}

/**
 * Generic credential-based status converter (WinFactor, Compass, TAFS)
 */
function convertCredentialProviderStatus(
    status: FactoringProviderStatus | undefined,
    providerLabel: string,
    defaultStatus: WinfactorAuthStatus | CompassAuthStatus | TafsAuthStatus
): WinfactorAuthStatus | CompassAuthStatus | TafsAuthStatus {
    if (!status || !status.connected) {
        return { ...defaultStatus, message: `Please configure ${providerLabel} credentials` }
    }
    if (status.connected && status.isValid) {
        return {
            isAuthenticated: true,
            hasCredentials: true,
            status: 'authenticated',
            message: `Connected to ${providerLabel}`,
            needsReauth: false,
        } as WinfactorAuthStatus
    }
    return {
        isAuthenticated: false,
        hasCredentials: true,
        status: 'not_authenticated',
        message: `${providerLabel} credentials may be invalid`,
        needsReauth: true,
    } as WinfactorAuthStatus
}

/**
 * Convert backend WinFactor status to WinfactorAuthStatus
 */
function convertWinfactorStatus(status: FactoringProviderStatus | undefined): WinfactorAuthStatus {
    if (!status || !status.connected) {
        return {
            ...DEFAULT_WINFACTOR_STATUS,
            message: 'Please configure PRO Funding credentials',
        }
    }

    if (status.connected && status.isValid) {
        return {
            isAuthenticated: true,
            hasCredentials: true,
            status: 'authenticated',
            message: 'Connected to PRO Funding',
            needsReauth: false,
        }
    }

    return {
        isAuthenticated: false,
        hasCredentials: true,
        status: 'not_authenticated',
        message: 'PRO Funding credentials may be invalid',
        needsReauth: true,
    }
}

/**
 * Check WinFactor auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkWinfactorAuthStatus(): Promise<WinfactorAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertWinfactorStatus(status?.winfactor)
}

/**
 * Check GSquared auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkGsquaredAuthStatus(): Promise<GsquaredAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertCredentialProviderStatus(status?.gsquared, 'GSquared', DEFAULT_GSQUARED_STATUS) as GsquaredAuthStatus
}

/**
 * Save GSquared credentials via backend API
 */
export async function saveGsquaredCredentials(username: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.post('/factoring/gsquared/credentials', { username, password })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to save credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to save GSquared credentials' }
    }
}

/**
 * Remove GSquared credentials via backend API
 */
export async function removeGsquaredCredentials(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/gsquared/credentials')
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove GSquared credentials' }
    }
}

export async function saveCapitalDepotCredentials(username: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.post('/factoring/capital-depot/credentials', { username, password })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to save credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError.response?.data?.message || 'Failed to save Capital Depot credentials' }
    }
}

export async function removeCapitalDepotCredentials(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/capital-depot/credentials')
        if (response.data?.success) {
            clearFactoringStatusCache()
            clearFactoringDataCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError.response?.data?.message || 'Failed to remove Capital Depot credentials' }
    }
}

/**
 * Check Compass auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkCompassAuthStatus(): Promise<CompassAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertCredentialProviderStatus(status?.compass, 'Compass', DEFAULT_COMPASS_STATUS) as CompassAuthStatus
}

/**
 * Check TAFS auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkTafsAuthStatus(): Promise<TafsAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertCredentialProviderStatus(status?.tafs, 'TAFS', DEFAULT_TAFS_STATUS) as TafsAuthStatus
}

/**
 * Check Engaged Financial "auth" — always authenticated (public API)
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkEngagedFinancialAuthStatus(): Promise<EngagedFinancialAuthStatus> {
    return DEFAULT_ENGAGED_FINANCIAL_STATUS
}

/**
 * Check HaulPay auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkHaulPayAuthStatus(): Promise<HaulPayAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertCredentialProviderStatus(status?.haul_pay, 'HaulPay', DEFAULT_HAUL_PAY_STATUS) as HaulPayAuthStatus
}

/**
 * Save HaulPay credentials via backend API
 */
export async function saveHaulPayCredentials(username: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.post('/factoring/haul-pay/credentials', { username, password })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to save credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to save HaulPay credentials' }
    }
}

/**
 * Remove HaulPay credentials via backend API
 */
export async function removeHaulPayCredentials(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/haul-pay/credentials')
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove HaulPay credentials' }
    }
}

/**
 * Save WinFactor credentials via backend API
 */
export async function saveWinfactorCredentials(
    username: string,
    password: string,
    tokenPurpose?: 'accounting' | 'rating'
): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.post('/factoring/winfactor/credentials', { username, password, tokenPurpose })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to save credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to save credentials' }
    }
}

/**
 * Remove RTS token for this project (disconnect accounting factoring)
 */
export async function removeRtsToken(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/rts/tokens', { params: { tokenPurpose: 'accounting' } })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove RTS token' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove RTS token' }
    }
}

/**
 * Remove Triumph token for this project (disconnect accounting factoring)
 */
export async function removeTriumphToken(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/triumph/tokens')
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove Triumph token' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove Triumph token' }
    }
}

/**
 * Remove the accounting WinFactor connection via backend API. Settings and Invoices are its only callers; the server reads a
 * disconnect that names no purpose as the extension's rating login.
 */
export async function removeWinfactorCredentials(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/winfactor/credentials', { params: { tokenPurpose: 'accounting' } })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove credentials' }
    }
}

/**
 * Save Compass credentials via backend API (reference: extension SAVE_COMPASS_CREDENTIALS)
 */
export async function saveCompassCredentials(username: string, password: string, role?: string): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.post('/factoring/compass/credentials', { username, password, role })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to save credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to save credentials' }
    }
}

/**
 * Remove Compass credentials via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function removeCompassCredentials(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/compass/credentials')
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove credentials' }
    }
}

/**
 * Save TAFS credentials via backend API (reference: extension SAVE_TAFS_CREDENTIALS)
 */
export async function saveTafsCredentials(
    username: string,
    password: string,
    tokenPurpose: 'accounting' | 'rating' = 'rating'
): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.post('/factoring/tafs/credentials', { username, password, tokenPurpose })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to save credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to save credentials' }
    }
}

/**
 * Check Stratopay auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkStratopayAuthStatus(): Promise<StratopayAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertCredentialProviderStatus(status?.stratopay, 'Stratopay', DEFAULT_STRATOPAY_STATUS) as StratopayAuthStatus
}

/**
 * Save Stratopay credentials via backend API
 */
export async function saveStratopayCredentials(
    username: string,
    password: string,
    oauthClientId?: string,
    oauthClientSecret?: string,
    tokenPurpose: 'accounting' | 'rating' = 'accounting'
): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.post('/factoring/stratopay/credentials', { username, password, oauthClientId, oauthClientSecret, tokenPurpose })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to save credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to save credentials' }
    }
}

/**
 * Remove Stratopay credentials via backend API
 */
export async function removeStratopayCredentials(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/stratopay/credentials')
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove credentials' }
    }
}

/**
 * Remove Artispro credentials via backend API.
 * Artispro is connected by harvesting Cognito tokens through the extension; disconnect simply
 * deletes the stored credentials and clears the active provider on the backend.
 */
/**
 * Test the Artispro connection for real — a token exchange, not a database read.
 *
 * The status endpoints answer from stored credentials, which is why a dead connection could keep
 * showing a green "Connected" chip. Clicking that chip means "check it now", so this asks the server
 * to actually exchange the token; a rejection is recorded server-side, and the next status read
 * reports the connection as expired.
 */
export async function verifyArtisproConnection(): Promise<{ success: boolean; isValid?: boolean; needsReauth?: boolean; message?: string }> {
    try {
        const response = await axios.get('/factoring/artispro/verify')
        clearFactoringStatusCache()
        const data = response.data?.data
        return { success: true, isValid: data?.isValid === true, needsReauth: data?.needsReauth === true }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, message: axiosError?.response?.data?.message || 'Failed to verify Artispro' }
    }
}

export async function removeArtisproCredentials(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/artispro/credentials')
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove credentials' }
    }
}

/**
 * Remove Outgo credentials via backend API.
 * Outgo is connected via an OAuth tab through the extension; disconnect deletes the stored
 * credentials and clears the active provider on the backend. The connection is saved under the
 * accounting purpose, so scope the delete to it.
 */
export async function removeOutgoCredentials(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/outgo/credentials', { params: { tokenPurpose: 'accounting' } })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove credentials' }
    }
}

/**
 * Remove TAFS credentials via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function removeTafsCredentials(tokenPurpose: 'accounting' | 'rating' = 'rating'): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/tafs/credentials', { params: { tokenPurpose } })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove credentials' }
    }
}

/**
 * Check OTR Solutions auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkOtrSolutionsAuthStatus(): Promise<OtrSolutionsAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertCredentialProviderStatus(status?.otr_solutions, 'OTR Solutions', DEFAULT_OTR_SOLUTIONS_STATUS) as OtrSolutionsAuthStatus
}

/**
 * Save OTR Solutions credentials via backend API.
 *
 * `accounting` connects OTR for invoice submission (Carrier TMS API) and additionally accepts the
 * APIM subscription key and the carrier DOT; `rating` connects the portal for broker credit checks
 * and ignores both. The DOT defaults to the company profile when omitted.
 */
export async function saveOtrSolutionsCredentials(
    username: string,
    password: string,
    tokenPurpose: 'accounting' | 'rating' = 'rating',
    extras?: { subscriptionKey?: string; clientDot?: string }
): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.post('/factoring/otr-solutions/credentials', {
            username,
            password,
            tokenPurpose,
            subscriptionKey: extras?.subscriptionKey || undefined,
            clientDot: extras?.clientDot || undefined,
        })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to save credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to save OTR Solutions credentials' }
    }
}

/**
 * Remove OTR Solutions credentials via backend API
 */
export async function removeOtrSolutionsCredentials(tokenPurpose: 'accounting' | 'rating' = 'rating'): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/otr-solutions/credentials', { params: { tokenPurpose } })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove OTR Solutions credentials' }
    }
}

/**
 * Remove WEX cookies for this project (disconnect)
 */
export async function removeWexCredentials(): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/wex/cookies', { params: { purpose: 'accounting' } })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove WEX credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove WEX credentials' }
    }
}

/** Settings disconnects the accounting connection; `'rating'` disconnects the one ratings use, as for TAFS and OTR. */
export async function removeEcapitalSession(tokenPurpose: 'accounting' | 'rating' = 'accounting'): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/ecapital/session', { params: { tokenPurpose } })
        if (response.data?.success) {
            clearFactoringStatusCache()
            clearEcapitalSessionCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove eCapital session' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove eCapital session' }
    }
}

/**
 * Check Flexent auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkFlexentAuthStatus(): Promise<FlexentAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertCredentialProviderStatus(status?.flexent, 'Flexent', DEFAULT_FLEXENT_STATUS) as FlexentAuthStatus
}

/**
 * Save Flexent credentials via backend API. `username` accepts the portal's email OR its numeric
 * username — Flexent's login field is `emailOrUsername` and takes either.
 */
export async function saveFlexentCredentials(username: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.post('/factoring/flexent/credentials', { username, password })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to save Flexent credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to save Flexent credentials' }
    }
}

/**
 * Remove Flexent credentials via backend API. Scoped to one purpose so disconnecting the rating
 * connection can never take an accounting connection down with it.
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function removeFlexentCredentials(tokenPurpose: 'accounting' | 'rating' = 'rating'): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/flexent/credentials', { params: { tokenPurpose } })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove Flexent credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove Flexent credentials' }
    }
}

/**
 * Check BasicBlock auth status via backend API
 *
 * @public — per-provider status/disconnect API. No caller yet; the UI reads the
 * combined status through getAuthenticatedProviders. Kept as the documented
 * per-provider surface (see README) rather than deleted.
 */
export async function checkBasicblockAuthStatus(): Promise<BasicblockAuthStatus> {
    const status = await fetchFactoringStatus()
    return convertCredentialProviderStatus(status?.basicblock, 'BasicBlock', DEFAULT_BASICBLOCK_STATUS) as BasicblockAuthStatus
}

/**
 * Save BasicBlock credentials via backend API. `username` is the carrier portal email — BasicBlock
 * signs in through Firebase, whose `signInWithPassword` takes an email address and nothing else.
 */
export async function saveBasicblockCredentials(
    username: string,
    password: string,
    tokenPurpose: 'accounting' | 'rating' = 'rating'
): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.post('/factoring/basicblock/credentials', { username, password, tokenPurpose })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to save BasicBlock credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to save BasicBlock credentials' }
    }
}

/**
 * Remove BasicBlock credentials via backend API. Scoped to one purpose so disconnecting the rating
 * connection can never take an accounting connection down with it.
 */
export async function removeBasicblockCredentials(tokenPurpose: 'accounting' | 'rating' = 'rating'): Promise<{ success: boolean; error?: string }> {
    try {
        const response = await axios.delete('/factoring/basicblock/credentials', { params: { tokenPurpose } })
        if (response.data?.success) {
            clearFactoringStatusCache()
            return { success: true }
        }
        return { success: false, error: response.data?.message || 'Failed to remove BasicBlock credentials' }
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        return { success: false, error: axiosError?.response?.data?.message || 'Failed to remove BasicBlock credentials' }
    }
}

/**
 * Clear the status cache (useful after authentication). Routine reads (page mount, every refocus) call this before
 * reading, so it must NOT reset the remembered eCapital portal check: that would make every read wait on the extension.
 * The portal check is reset only where the login really changes — see `clearEcapitalSessionCache`.
 */
export function clearFactoringStatusCache(): void {
    statusCache = { data: null, timestamp: 0 }
}
